import React, { useEffect, useState } from 'react';
import '../styles/common/Spinner.css';
import '../styles/ScoreCards.css';
import '../styles/common/Modal.css';
import MusicalNoteIcon from './icons/musical-note'
import CardMenu from './CardMenu';
import DeleteScoreModal from './DeleteScoreModal';
import { PiMicrophoneStageFill } from "react-icons/pi";
import Modal from './common/Modal';
import { useScores } from '../hooks/useScores';
import { filterScores, groupScoresByArtist, sortScores } from '../utils/scoreUtils';
import { useScoreFilters } from '../context/ScoreFiltersContext';
import { getStoredUserProfile } from '../utils/authStorage';
import { buildApiUrl } from '../lib/api';
import { DEFAULT_SCORE_ARTIST, DEFAULT_SCORE_TITLE } from '../utils/scoreDefaults';

const ScoreCards = ({ onNotify, refreshKey = 0, userId }) => {
    const { scores, setScores, loading } = useScores({ userId, refreshKey });
    const { query, sortKey } = useScoreFilters();
    const [openMenuIndex, setOpenMenuIndex] = useState(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null); // { scoreId, title }
    const [isRenameOpen, setIsRenameOpen] = useState(false);
    const [pendingRename, setPendingRename] = useState(null); // { scoreId, title, artist }
    const [renameTitle, setRenameTitle] = useState("");
    const [renameArtist, setRenameArtist] = useState("");
    const [isMutating, setIsMutating] = useState(false);

    const toggleMenu = (index) => {
        setOpenMenuIndex(openMenuIndex === index ? null : index);
    };

    const openConfirm = (score) => {
        setOpenMenuIndex(null);
        setPendingDelete({ scoreId: score.scoreId, title: score.title, artist: score.artist });
        setIsConfirmOpen(true);
    };

    const closeConfirm = () => {
        setIsConfirmOpen(false);
        setPendingDelete(null);
    };

    const openRename = (score) => {
      setOpenMenuIndex(null);
      setPendingRename({ scoreId: score.scoreId, title: score.title, artist: score.artist });
      setRenameTitle(score.title || "");
      setRenameArtist(score.artist || "");
      setIsRenameOpen(true);
    };

    const closeRename = () => {
      setIsRenameOpen(false);
      setPendingRename(null);
      setRenameTitle("");
      setRenameArtist("");
    };

    const handleConfirmRename = async () => {
      const storedUser = getStoredUserProfile();
      const resolvedUserId = userId || storedUser?.uid || storedUser?.sub;
      if (!resolvedUserId || !pendingRename?.scoreId || !renameTitle.trim()) return;
      try {
        setIsMutating(true);
        const res = await fetch(buildApiUrl('/rename-score'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: resolvedUserId,
            scoreId: pendingRename.scoreId,
            title: renameTitle.trim(),
            artist: renameArtist.trim(),
          })
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setScores(prev =>
          prev.map(s =>
            s.scoreId === pendingRename.scoreId
              ? { ...s, title: renameTitle.trim(), artist: renameArtist.trim() }
              : s
          )
        );
        closeRename();
        onNotify?.({ title: 'Updated', message: 'Score updated successfully.', variant: 'success' });
      } catch (e) {
        console.error('Failed to rename score:', e);
        onNotify?.({ title: 'Error', message: 'Failed to rename score.', variant: 'error' });
      } finally {
        setIsMutating(false);
      }
    };

    useEffect(() => {
      const onDocClick = () => setOpenMenuIndex(null);
      document.addEventListener('click', onDocClick);
      return () => document.removeEventListener('click', onDocClick);
    }, []);

    const filteredScores = React.useMemo(() => {
      const filtered = filterScores(scores, query);
      return sortScores(filtered, sortKey);
    }, [scores, query, sortKey]);

    if (loading || isMutating) {
        return (
            <div className="spinner-container">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!filteredScores.length) {
        return <div className="score-empty-state">No scores found</div>;
    }

    const handleCardClick = (scoreId) => {
        window.location.href = `/?scoreid=${scoreId}`;
    };

    const artistEntries = groupScoresByArtist(filteredScores);

    return (
        <div className="score-card-groups">
            {artistEntries.map(([artistName, artistScores], groupIndex) => (
              <section
                className="artist-group"
                key={artistName}
                style={{ '--group-index': groupIndex }}
              >
                <div className="artist-group-header">
                  <PiMicrophoneStageFill className="artist-group-icon" />
                  <span className="artist-group-name">{artistName}</span>
                  <span className="artist-group-count">{artistScores.length}</span>
                </div>
                <div className="score-cards-container">
                  {artistScores.map((score, index) => (
                    <div
                      className="score-card"
                      key={score.scoreId}
                      style={{ '--card-index': index }}
                      onClick={() => handleCardClick(score.scoreId)}
                      onMouseLeave={() => {
                        if (openMenuIndex === `${artistName}-${index}`) setOpenMenuIndex(null);
                      }}
                    >
                        <CardMenu
                          isOpen={openMenuIndex === `${artistName}-${index}`}
                          onToggle={() => toggleMenu(`${artistName}-${index}`)}
                          onRename={(e) => { e?.stopPropagation?.(); openRename(score); }}
                          onDelete={(e) => { e?.stopPropagation?.(); openConfirm(score); }}
                          scoreId={score.scoreId}
                        />
                        <div className="score-card-icon">
                            <MusicalNoteIcon />
                        </div>
                        <div className="score-card-title">{score.title?.trim() || DEFAULT_SCORE_TITLE}</div>
                        <div className="score-card-artist">
                          {score.artist?.trim() || DEFAULT_SCORE_ARTIST}
                        </div>
                        <div className="score-card-date">
                          {(score.updatedAt || score.score?.updatedAt || score.createdAt || "").slice(0, 10)}
                        </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
            {isConfirmOpen && pendingDelete && (
              <DeleteScoreModal
                scoreId={pendingDelete.scoreId}
                title={pendingDelete.title}
                artist={pendingDelete.artist}
                onClose={closeConfirm}
                onDeleted={(deletedId) => {
                  setScores((prev) => prev.filter((s) => s.scoreId !== deletedId));
                  closeConfirm();
                }}
                onNotify={onNotify}
                userId={userId}
              />
            )}
            {isRenameOpen && (
              <Modal
                isOpen
                onClose={closeRename}
                contentClassName="modal-content--modern"
                contentProps={{
                  role: 'dialog',
                  'aria-modal': 'true',
                  'aria-labelledby': 'rename-title',
                  tabIndex: -1,
                  onKeyDown: (e) => {
                    if (e.key === 'Escape') closeRename();
                  },
                }}
              >
                <h2 id="rename-title">Rename score</h2>
                <div style={{ marginTop: 8 }}>
                  <label className="modal-field-label">Title</label>
                  <input
                    autoFocus
                    type="text"
                    value={renameTitle}
                    onChange={(e) => setRenameTitle(e.target.value)}
                    className="form-control"
                  />
                </div>
                <div style={{ marginTop: 12 }}>
                  <label className="modal-field-label">Artist</label>
                  <input
                    type="text"
                    value={renameArtist}
                    onChange={(e) => setRenameArtist(e.target.value)}
                    className="form-control"
                  />
                </div>
                <div className="modal-buttons">
                  <button className="cancel" onClick={closeRename}>Cancel</button>
                  <button className="create" onClick={handleConfirmRename}>Update</button>
                </div>
              </Modal>
            )}
        </div>
    );
};

export default ScoreCards;
